import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import config from '../../config';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Paper,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  SelectChangeEvent,
  Snackbar,
} from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Search as SearchIcon,
  TrendingUp as AnalyticsIcon,
  Language as BingIcon,
  BugReport as CrawlIcon,
  PushPin as PinterestIcon,
} from '@mui/icons-material';
import { siteIntegrationService } from '../../services/siteIntegrationService';
import { metricsService } from '../../services/metricsService';
import { useSiteStore } from '../../store/site.store';
import { 
  GSCSetupPanel,
  GA4SetupPanel,
  BingSetupPanel,
  CrawlLogsSetupPanel,
  PinterestSetupPanel
} from '../../components/webtools';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`webtool-tabpanel-${index}`}
      aria-labelledby={`webtool-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

type ToolKey = 'GSC' | 'GA4' | 'BING' | 'PINTEREST' | 'CRAWL';

const isWebtoolEnabled = (flag: string) => flag.trim().toLowerCase() !== 'false';

export const WebToolsConfiguration: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentTab, setCurrentTab] = useState(0);

  // Derive which tools are enabled from environment flags
  const enabledTools = useMemo<ToolKey[]>(() => {
    const all: { key: ToolKey; flag: string }[] = [
      { key: 'GSC',       flag: config.VITE_ENABLE_WEBTOOL_GSC },
      { key: 'GA4',       flag: config.VITE_ENABLE_WEBTOOL_GA4 },
      { key: 'BING',      flag: config.VITE_ENABLE_WEBTOOL_BING },
      { key: 'PINTEREST', flag: config.VITE_ENABLE_WEBTOOL_PINTEREST },
      { key: 'CRAWL',     flag: config.VITE_ENABLE_WEBTOOL_CRAWL },
    ];
    return all.filter(t => isWebtoolEnabled(t.flag)).map(t => t.key);
  }, []);

  // Dynamic tab-index map based on enabled tools
  const toolTabMap = useMemo<Record<string, number>>(() => {
    const map: Record<string, number> = {};
    enabledTools.forEach((key, idx) => { map[key] = idx; });
    return map;
  }, [enabledTools]);

  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });
  const queryClient = useQueryClient();

  // Use global site store instead of local state to ensure consistency with other components
  const selectedSite = useSiteStore(state => state.selectedSite);
  const setSite = useSiteStore(state => state.setSite);
  const selectedSiteId = selectedSite?.id;

  // Handle OAuth callback parameters
  useEffect(() => {
    const oauthSuccess = searchParams.get('oauth_success');
    const oauthError = searchParams.get('oauth_error');
    const tool = searchParams.get('tool');
    const siteId = searchParams.get('siteId');

    if (oauthSuccess === 'true' && tool) {
      setSnackbar({
        open: true,
        message: `${tool} connected successfully!`,
        severity: 'success',
      });
      // Switch to the relevant tab
      if (toolTabMap[tool] !== undefined) {
        setCurrentTab(toolTabMap[tool]);
      }
      // Refresh data health status
      queryClient.invalidateQueries({ queryKey: ['dataSourceHealth'] });
      // Clear URL params
      setSearchParams({});
    } else if (oauthError) {
      setSnackbar({
        open: true,
        message: `${tool || 'OAuth'} connection failed: ${oauthError}`,
        severity: 'error',
      });
      // Switch to the relevant tab if tool is specified
      if (tool && toolTabMap[tool] !== undefined) {
        setCurrentTab(toolTabMap[tool]);
      }
      // Clear URL params
      setSearchParams({});
    }
  }, [searchParams, setSearchParams, queryClient]);

  // Fetch sites - single call on mount, cached for entire session
  const { data: sites } = useQuery({
    queryKey: ['sites'],
    queryFn: () => metricsService.getSites(),
    staleTime: Infinity, // Sites rarely change, cache indefinitely
    gcTime: Infinity,
  });

  // Auto-select first site and sync with global store
  // Also update selectedSite if it exists but has stale data
  useEffect(() => {
    if (sites && sites.length > 0) {
      if (!selectedSite) {
        // No site selected, select the first one
        setSite(sites[0], queryClient);
      } else if (selectedSite) {
        // Site is selected, but update it with fresh data from API
        const freshSite = sites.find(s => s.id === selectedSite.id);
        if (freshSite && freshSite.name !== selectedSite.name) {
          // Data has changed, update the store with fresh data
          setSite(freshSite, queryClient);
        } else if (!freshSite) {
          // Selected site no longer exists, select the first one
          setSite(sites[0], queryClient);
        }
      }
    }
  }, [sites, selectedSite, setSite, queryClient]);

  // Fetch integration status - React Query handles deduplication automatically
  const { data: integrationData, isLoading } = useQuery({
    queryKey: ['siteIntegrations', selectedSiteId],
    queryFn: () => selectedSiteId ? siteIntegrationService.getIntegrationStatus(selectedSiteId) : null,
    enabled: !!selectedSiteId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  const handleSiteChange = (event: SelectChangeEvent<number | ''>) => {
    const siteId = event.target.value as number;
    const site = sites?.find(s => s.id === siteId);
    if (site) {
      setSite(site, queryClient);
    }
  };

  const selectedSiteName = selectedSite?.name;
  const selectedSiteUrl = selectedSite?.url;

  // Extract integration status for each tool
  const gscStatus = integrationData?.gsc;
  const ga4Status = integrationData?.ga4;
  const bingStatus = integrationData?.bing;
  const pinterestStatus = integrationData?.pinterest;

  // Count completed/pending tools (connected and not expired)
  const completedCount = integrationData?.configuredCount || 0;
  const totalCount = enabledTools.length;

  return (
    <Box sx={{ 
      maxWidth: { xs: 400, sm: 768, md: 900, lg: 1200 },
      mx: 'auto',
      px: { xs: 2, sm: 3, md: 4 },
      py: 3
    }}>
      <Box mb={4}>
        <Typography variant="h4" component="h1" gutterBottom>
          Site Configuration
        </Typography>
        <Typography variant="body1" color="textSecondary">
          Configure and manage web tool integrations for your selected site. Each site has its own configuration.
        </Typography>
      </Box>

      <Box>
        {/* Site Selector */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Select Site</InputLabel>
                <Select
                  value={selectedSiteId || ''}
                  onChange={handleSiteChange}
                  label="Select Site"
                >
                  {Array.isArray(sites) && sites.map((site) => (
                    <MenuItem key={site.id} value={site.id}>
                      {site.name} ({site.url})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              {selectedSiteName && (
                <Alert severity="info" sx={{ mb: 0 }}>
                  <Typography variant="body2">
                    Configuring data sources for: <strong>{selectedSiteName}</strong>
                  </Typography>
                  <Typography variant="caption" display="block">
                    {selectedSiteUrl}
                  </Typography>
                </Alert>
              )}
            </Grid>
          </Grid>
        </Paper>

        {/* Progress Alert */}
        <Alert 
          severity={completedCount === totalCount ? 'success' : 'info'}
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          <Typography variant="body2">
            <strong>Configuration Progress:</strong> {completedCount} of {totalCount} tools configured.
            {completedCount < totalCount && ' Complete remaining setups to unlock all features.'}
            {completedCount === totalCount && ' All web tools are configured and operational! 🎉'}
          </Typography>
        </Alert>

        {/* Tabs */}
        {enabledTools.length === 0 ? (
          <Alert severity="warning">
            All web tool integrations are currently disabled via environment configuration.
          </Alert>
        ) : (
        <Paper sx={{ mb: 3 }}>
          <Tabs
            value={currentTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            {enabledTools.includes('GSC') && (
              <Tab
                icon={<SearchIcon />}
                label="Google Search Console"
                iconPosition="start"
                sx={{ textTransform: 'none', fontWeight: 600, opacity: gscStatus?.connected && !gscStatus?.expired ? 1 : 0.7 }}
              />
            )}
            {enabledTools.includes('GA4') && (
              <Tab
                icon={<AnalyticsIcon />}
                label="Google Analytics 4"
                iconPosition="start"
                sx={{ textTransform: 'none', fontWeight: 600, opacity: ga4Status?.connected && !ga4Status?.expired ? 1 : 0.7 }}
              />
            )}
            {enabledTools.includes('BING') && (
              <Tab
                icon={<BingIcon />}
                label="Bing Webmaster"
                iconPosition="start"
                sx={{ textTransform: 'none', fontWeight: 600, opacity: bingStatus?.connected && !bingStatus?.expired ? 1 : 0.7 }}
              />
            )}
            {enabledTools.includes('PINTEREST') && (
              <Tab
                icon={<PinterestIcon />}
                label="Pinterest"
                iconPosition="start"
                sx={{ textTransform: 'none', fontWeight: 600, opacity: pinterestStatus?.connected && !pinterestStatus?.expired ? 1 : 0.7 }}
              />
            )}
            {enabledTools.includes('CRAWL') && (
              <Tab
                icon={<CrawlIcon />}
                label="Crawl Logs"
                iconPosition="start"
                sx={{ textTransform: 'none', fontWeight: 600, opacity: 0.5 }}
                disabled
              />
            )}
          </Tabs>

          {/* Tab Panels — index is position within enabled tools */}
          {enabledTools.includes('GSC') && (
            <TabPanel value={currentTab} index={toolTabMap['GSC']}>
              <GSCSetupPanel
                integrationStatus={gscStatus}
                isLoading={isLoading}
                siteId={selectedSiteId}
                siteName={selectedSiteName}
              />
            </TabPanel>
          )}
          {enabledTools.includes('GA4') && (
            <TabPanel value={currentTab} index={toolTabMap['GA4']}>
              <GA4SetupPanel
                integrationStatus={ga4Status}
                isLoading={isLoading}
                siteId={selectedSiteId}
                siteName={selectedSiteName}
              />
            </TabPanel>
          )}
          {enabledTools.includes('BING') && (
            <TabPanel value={currentTab} index={toolTabMap['BING']}>
              <BingSetupPanel
                integrationStatus={bingStatus}
                isLoading={isLoading}
                siteId={selectedSiteId}
                siteName={selectedSiteName}
              />
            </TabPanel>
          )}
          {enabledTools.includes('PINTEREST') && (
            <TabPanel value={currentTab} index={toolTabMap['PINTEREST']}>
              <PinterestSetupPanel
                integrationStatus={pinterestStatus}
                isLoading={isLoading}
                siteId={selectedSiteId}
                siteName={selectedSiteName}
              />
            </TabPanel>
          )}
          {enabledTools.includes('CRAWL') && (
            <TabPanel value={currentTab} index={toolTabMap['CRAWL']}>
              <CrawlLogsSetupPanel
                integrationStatus={null}
                isLoading={isLoading}
                siteId={selectedSiteId}
                siteName={selectedSiteName}
              />
            </TabPanel>
          )}
        </Paper>
        )}
      </Box>

      {/* OAuth Status Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
